import { buildTones, HEX_COLOR, type Scheme } from './tones';

export type ThemeMode = 'system' | 'light' | 'dark';

/**
 * The ready colours a member can pick for themself (ADR-050, ADR-060): the project's, the employer's
 * petrol and champagne, then the five from the redesign brief. Each is only the seed of a tonal
 * palette (tones.ts). `petrol` is the
 * default (employer, 2026-11-01), whose tones are the plain tokens of index.css.
 */
export const ACCENT_SEED = {
  petrol: '#173f43',
  blue: '#1858c4',
  rose: '#a3234f',
  copper: '#9c4712',
  graphite: '#2f3843',
  forest: '#3f6416',
  azure: '#386bd6',
  violet: '#8056d9',
  teal: '#138c77',
  pink: '#c64d92',
  amber: '#c97728',
} as const;
export type AccentName = keyof typeof ACCENT_SEED;
export const ACCENTS = Object.keys(ACCENT_SEED) as AccentName[];

const DEFAULT_ACCENT: AccentName = 'petrol';

/** A ready colour by name, or a `#rrggbb` the member chose. */
export type Accent = AccentName | `#${string}`;

/** The home page's two views for a member who has both (ADR-051). */
export type HomeView = 'today' | 'manage';

const KEY = 'clinic-theme';
const ACCENT_KEY = 'clinic-accent';
const TONES_KEY = 'clinic-tones';
const LOOKS_KEY = 'clinic-looks-2';
const OLD_LOOKS_KEY = 'clinic-looks';
const HOME_VIEW_KEY = 'clinic-home-view';
const RAIL_KEY = 'clinic-rail';
const ORDER: readonly ThemeMode[] = ['system', 'light', 'dark'];

/**
 * The app's only use of web storage (ADR-047, ADR-050, ADR-051, ADR-060): words of display
 * preference and the colours worked out from them, never data or tokens, so online-first is
 * untouched. Blocked storage (private mode) only means not remembered.
 */
function storage(): Storage | null {
  try {
    // eslint-disable-next-line no-restricted-globals -- ADR-047/050/051/060: display preferences only.
    return localStorage;
  } catch {
    return null;
  }
}

function read(key: string): string | null {
  try {
    return storage()?.getItem(key) ?? null;
  } catch {
    return null;
  }
}

function write(key: string, value: string | null) {
  try {
    if (value === null) storage()?.removeItem(key);
    else storage()?.setItem(key, value);
  } catch {
    // Not remembered on this device; it still applies until the page is closed.
  }
}

const isMode = (value: unknown): value is ThemeMode => value === 'system' || value === 'light' || value === 'dark';
const isAccent = (value: unknown): value is Accent =>
  typeof value === 'string' && (value in ACCENT_SEED || HEX_COLOR.test(value));

function stored(): ThemeMode {
  const value = read(KEY);
  return value === 'light' || value === 'dark' ? value : 'system';
}

function storedAccent(): Accent {
  const value = read(ACCENT_KEY);
  return isAccent(value) ? value : DEFAULT_ACCENT;
}

export const seedOf = (accent: Accent): string => (accent in ACCENT_SEED ? ACCENT_SEED[accent as AccentName] : accent);

interface Look {
  mode: ThemeMode;
  accent: Accent;
}

/** Each member's own look on this device, by user id, so a shared desk computer keeps them apart. */
function looks(): Record<string, Look> {
  try {
    const value: unknown = JSON.parse(read(LOOKS_KEY) ?? 'null');
    return value && typeof value === 'object' ? (value as Record<string, Look>) : earlierLooks();
  } catch {
    return {};
  }
}

/**
 * The record of the version before petrol became the default wrote `blue` for every member who
 * had chosen nothing, and a choice of blue looks the same. Blue was the default, so those members
 * move to the new default with everyone else; any other colour was a real choice and stays.
 */
function earlierLooks(): Record<string, Look> {
  try {
    const value: unknown = JSON.parse(read(OLD_LOOKS_KEY) ?? '{}');
    if (!value || typeof value !== 'object') return {};
    const moved = Object.fromEntries(
      Object.entries(value as Record<string, Look>).map(([id, look]) => [
        id,
        look.accent === 'blue' ? { ...look, accent: DEFAULT_ACCENT } : look,
      ]),
    ) as Record<string, Look>;
    // The new record first: the old one goes only once nobody's choice depends on it.
    write(LOOKS_KEY, JSON.stringify(moved));
    write(OLD_LOOKS_KEY, null);
    return moved;
  } catch {
    return {};
  }
}

const deviceDark = () => typeof matchMedia === 'function' && matchMedia('(prefers-color-scheme: dark)').matches;

/**
 * `data-theme` on <html> picks the token set (index.css); without it the device decides. The
 * phone's status bar (`theme-color`) follows the same choice.
 */
function apply(mode: ThemeMode, accent: Accent) {
  const root = document.documentElement;
  if (mode === 'system') delete root.dataset.theme;
  else root.dataset.theme = mode;
  applyAccent(mode, accent);
  const seed = seedOf(accent);
  const metas = [...document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"][media]')];
  const groundOf = (scheme: Scheme) => buildTones(seed, scheme)['--bg'] ?? '';
  // The attribute, not `meta.media`: older browsers do not have the property.
  for (const meta of metas) {
    const own: Scheme = meta.getAttribute('media')?.includes('dark') ? 'dark' : 'light';
    meta.content = groundOf(mode === 'system' ? own : mode);
  }
}

/**
 * The default colour is the plain tokens; any other is its tones, set on <html> for the scheme on
 * screen. A copy of both schemes goes to storage for public/theme-boot.js to replay before the
 * first paint.
 */
function applyAccent(mode: ThemeMode, accent: Accent) {
  const root = document.documentElement;
  const owned = Object.keys(buildTones(ACCENT_SEED[DEFAULT_ACCENT], 'light'));
  if (accent === DEFAULT_ACCENT) {
    delete root.dataset.accent;
    for (const name of owned) root.style.removeProperty(name);
    write(TONES_KEY, null);
    return;
  }
  root.dataset.accent = accent.startsWith('#') ? 'custom' : accent;
  const seed = seedOf(accent);
  const scheme: Scheme = mode === 'system' ? (deviceDark() ? 'dark' : 'light') : mode;
  for (const [name, value] of Object.entries(buildTones(seed, scheme))) root.style.setProperty(name, value);
  write(TONES_KEY, JSON.stringify({ light: buildTones(seed, 'light'), dark: buildTones(seed, 'dark') }));
}

/**
 * Light, dark (the night shift) or the device's own setting (ADR-047), and a colour (ADR-050,
 * ADR-060). Each member's own choice, kept in this browser under their id and touching no data, so
 * one member's look never reaches another member on the same computer.
 */
class Theme {
  mode = $state<ThemeMode>(typeof window === 'undefined' ? 'system' : stored());
  accent = $state<Accent>(typeof window === 'undefined' ? DEFAULT_ACCENT : storedAccent());
  #user: string | null = null;

  /** The mode the switch moves to next. */
  get next(): ThemeMode {
    return ORDER[(ORDER.indexOf(this.mode) + 1) % ORDER.length] ?? 'system';
  }

  /** The seed on screen, for the colour field. */
  get seed(): string {
    return seedOf(this.accent);
  }

  set(mode: ThemeMode) {
    this.mode = mode;
    this.#store();
    apply(mode, this.accent);
  }

  setAccent(accent: Accent) {
    this.accent = accent;
    this.#store();
    apply(this.mode, accent);
  }

  /** Back to the look nobody chose: the device's light or dark and the default colour. */
  reset() {
    this.mode = 'system';
    this.accent = DEFAULT_ACCENT;
    this.#store();
    apply('system', DEFAULT_ACCENT);
  }

  /**
   * The member is known (after sign-in): their own look returns. The first member after this
   * version keeps what the device already had, which was theirs; any other starts plain.
   */
  bindUser(userId: string) {
    if (this.#user === userId) return;
    const all = looks();
    const first = Object.keys(all).length === 0;
    this.#user = userId;
    const mine = all[userId];
    if (mine && isMode(mine.mode) && isAccent(mine.accent)) {
      this.mode = mine.mode;
      this.accent = mine.accent;
    } else if (!first) {
      this.mode = 'system';
      this.accent = DEFAULT_ACCENT;
    }
    this.#store();
    apply(this.mode, this.accent);
  }

  /** What the device replays at the next start, and the member's own record. */
  #store() {
    write(KEY, this.mode === 'system' ? null : this.mode);
    write(ACCENT_KEY, this.accent === DEFAULT_ACCENT ? null : this.accent);
    if (this.#user) write(LOOKS_KEY, JSON.stringify({ ...looks(), [this.#user]: { mode: this.mode, accent: this.accent } }));
  }

  /** The device turned light or dark while the member follows it: the tones change with it. */
  deviceChanged() {
    if (this.mode === 'system' && this.accent !== DEFAULT_ACCENT) apply(this.mode, this.accent);
  }
}

export const theme = new Theme();

/**
 * Which view the home opens on for a member with both (ADR-051): «کار امروز» unless this device
 * remembers «مدیریت». A choice of the device, like the theme; it opens or hides no data.
 */
class HomeViewChoice {
  value = $state<HomeView>(typeof window !== 'undefined' && read(HOME_VIEW_KEY) === 'manage' ? 'manage' : 'today');

  set(view: HomeView) {
    this.value = view;
    write(HOME_VIEW_KEY, view === 'today' ? null : view);
  }
}

export const homeView = new HomeViewChoice();

/** The desktop rail, open or down to its icons (ADR-060); a choice of the device, like the view. */
class RailChoice {
  collapsed = $state(typeof window !== 'undefined' && read(RAIL_KEY) === 'collapsed');

  toggle() {
    this.collapsed = !this.collapsed;
    write(RAIL_KEY, this.collapsed ? 'collapsed' : null);
  }
}

export const rail = new RailChoice();

/** Applies the remembered theme before the app first renders (main.ts). */
export function startTheme(): void {
  apply(theme.mode, theme.accent);
  if (typeof matchMedia === 'function') {
    matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => theme.deviceChanged());
  }
}
